import type {
  Order,
  OrderEvent,
  OrderLine,
  OrderPaymentCard,
  OrderRefund,
  OrderSummary,
  OrderWorkflow,
} from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { money, type Money } from '@nucleus-commerce/core';

/** What is still owed; never negative even if a refund overshoots. */
export function outstanding(order: Pick<Order, 'total' | 'paid' | 'currency'>): Money {
  return money(Math.max(0, order.total.amount - order.paid.amount), order.currency);
}

/** Money received and not yet given back; what a refund may still take. */
export function refundable(order: Pick<Order, 'paid' | 'refunded' | 'currency'>): Money {
  return money(Math.max(0, order.paid.amount - order.refunded.amount), order.currency);
}

/** Mark-paid only applies while the backend would accept it (D-031). */
export function isAwaitingPayment(
  order: Pick<OrderSummary, 'financialStatus' | 'status'>,
): boolean {
  return (
    order.status !== 'cancelled' &&
    (order.financialStatus === 'unpaid' || order.financialStatus === 'partially_paid')
  );
}

export type CancelBlocker = 'cancelled' | 'fulfilled' | 'paid';

/** Why the backend would answer 409 to a cancel, in the order it checks. */
export function cancelBlocker(
  order: Pick<Order, 'status' | 'fulfillmentStatus' | 'paid' | 'refunded'>,
): CancelBlocker | null {
  if (order.status === 'cancelled') return 'cancelled';
  if (
    order.fulfillmentStatus === 'fulfilled' ||
    order.fulfillmentStatus === 'partially_fulfilled'
  ) {
    return 'fulfilled';
  }
  if (order.paid.amount - order.refunded.amount > 0) return 'paid';
  return null;
}

/** Units of a line nobody has shipped or cancelled yet. */
export function openQuantity(
  line: Pick<OrderLine, 'quantity' | 'fulfilledQuantity' | 'cancelledQuantity'>,
): number {
  return Math.max(0, line.quantity - line.fulfilledQuantity - line.cancelledQuantity);
}

export type FulfilBlocker = 'cancelled' | 'on_hold' | 'nothing_open';

/** Why the backend would refuse a new fulfilment (D-044), in the order it checks. */
export function fulfilBlocker(
  order: Pick<Order, 'status' | 'fulfillmentStatus' | 'lines'>,
): FulfilBlocker | null {
  if (order.status === 'cancelled' || order.status === 'archived') return 'cancelled';
  if (order.fulfillmentStatus === 'on_hold') return 'on_hold';
  if (!order.lines.some((line) => openQuantity(line) > 0)) return 'nothing_open';
  return null;
}

/** A refund needs money that was received and not yet returned. */
export function canRefund(
  order: Pick<Order, 'status' | 'paid' | 'refunded' | 'currency'>,
): boolean {
  return order.status !== 'archived' && refundable(order).amount > 0;
}

/** A hold parks fulfilment; it makes no sense once everything shipped or the order is closed. */
export function canHold(order: Pick<Order, 'status' | 'fulfillmentStatus'>): boolean {
  return (
    order.status === 'open' &&
    order.fulfillmentStatus !== 'on_hold' &&
    order.fulfillmentStatus !== 'fulfilled'
  );
}

/** The shipping address may change until fulfilment starts. */
export function canEditAddress(order: Pick<Order, 'status' | 'fulfillmentStatus'>): boolean {
  return (
    order.status !== 'cancelled' &&
    (order.fulfillmentStatus === 'unfulfilled' || order.fulfillmentStatus === 'on_hold')
  );
}

const DESIGN_SENDABLE: ReadonlySet<Order['approvalStatus']> = new Set([
  'awaiting_design',
  'design_sent',
  'changes_requested',
]);

/**
 * A design can go out while the shopper has not approved one yet. The backend also accepts a
 * resend while the previous design is still waiting for an answer; the link in the new email
 * replaces the old one.
 */
export function canSendDesign(order: Pick<Order, 'status' | 'approvalStatus' | 'lines'>): boolean {
  return (
    order.status === 'open' &&
    DESIGN_SENDABLE.has(order.approvalStatus) &&
    order.lines.some((line) => line.requiresDesignApproval)
  );
}

/**
 * While the production workflow runs, its stages own design approval and shipping; the legacy
 * Send design and Fulfil actions answer 409 WORKFLOW_OWNS_APPROVAL / WORKFLOW_OWNS_FULFILMENT.
 */
export function workflowOwnsOrder(order: Pick<OrderSummary, 'workflow'>): boolean {
  return order.workflow?.status === 'active';
}

export interface WorkflowControl {
  /** An approving review stage is open (`designApprovalStageKey`): Send design answers 409. */
  approval: boolean;
  /** The workflow runs and its shipping stage is not the current one: Fulfil answers 409. */
  fulfilment: boolean;
}

/**
 * What the running workflow takes over, read from the full workflow. Until it has loaded, an
 * active workflow is assumed to own both so the page never offers an action that would 409.
 */
export function workflowControl(
  order: Pick<OrderSummary, 'workflow'>,
  workflow:
    | Pick<OrderWorkflow, 'status' | 'currentStageKey' | 'designApprovalStageKey' | 'stages'>
    | undefined,
): WorkflowControl {
  if (!workflowOwnsOrder(order)) return { approval: false, fulfilment: false };
  if (!workflow) return { approval: true, fulfilment: true };
  if (workflow.status !== 'active') return { approval: false, fulfilment: false };
  const current = workflow.stages.find((stage) => stage.key === workflow.currentStageKey);
  return {
    approval: workflow.designApprovalStageKey !== null,
    fulfilment: current?.type !== 'shipping',
  };
}

/**
 * How the backend will move the money: a captured Stripe payment is refunded through Stripe
 * (pending until it confirms), a manual payment is recorded only, gift cards get their balance
 * back when nothing else was captured.
 */
export function refundKind(
  order: Pick<Order, 'payments' | 'giftCardApplications'>,
): OrderRefund['kind'] {
  const captured = order.payments.find(
    (p) => p.amountCaptured.amount - p.amountRefunded.amount > 0,
  );
  if (captured) return captured.provider === 'stripe' ? 'provider' : 'manual';
  return order.giftCardApplications.length > 0 ? 'gift_card' : 'manual';
}

/**
 * A plain 409 on any order action means the order moved on; codes with their own meaning
 * (WORKFLOW_OWNS_FULFILMENT, PAYMENT_PROVIDER_UNAVAILABLE) keep their catalog message.
 */
export function orderConflictKey(error: unknown, key: string): string | null {
  return error instanceof ApiError && error.kind === 'conflict' && error.code === 'CONFLICT'
    ? key
    : null;
}

/** Why a refund was refused, per code: the provider has no charge yet, or nothing is refundable. */
export function refundErrorKey(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.kind !== 'conflict') return null;
  if (error.code === 'PAYMENT_PROVIDER_UNAVAILABLE') {
    return 'orders:refund.errors.providerUnavailable';
  }
  return error.code === 'CONFLICT' ? 'orders:refund.errors.conflict' : null;
}

/** Order emails staff can send again; the workflow emails are resent from their stage. */
export const RESENDABLE_EMAILS: ReadonlySet<string> = new Set([
  'order.created',
  'order.paid',
  'order.partially_paid',
  'order.payment_reminder',
  'order.cancelled',
  'order.shipped',
  'order.delivered',
  'order.refunded',
  'order.design_sent',
  'return.approved',
  'return.rejected',
  'return.received',
  'return.resolved',
]);

/** 422 LIMIT_EXCEEDED carries `meta.retryAfterSeconds`; the copy rounds it up to minutes. */
export function resendErrorKey(
  error: unknown,
): { key: string; values: Record<string, unknown> } | null {
  if (!(error instanceof ApiError) || error.code !== 'LIMIT_EXCEEDED') return null;
  const seconds = Number(error.meta.retryAfterSeconds);
  return Number.isFinite(seconds) && seconds > 0
    ? { key: 'orders:resend.limitReached', values: { minutes: Math.ceil(seconds / 60) } }
    : { key: 'orders:resend.limitReachedNoTime', values: {} };
}

/**
 * Release refuses with 409 ORDER_APPROVAL_PENDING while the design is unapproved; a member with
 * `orders.edit` may release anyway.
 */
export function isApprovalPending(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'ORDER_APPROVAL_PENDING';
}

/** Archiving tidies away orders that are done: completed or cancelled. */
export function canArchive(order: Pick<Order, 'status'>): boolean {
  return order.status === 'completed' || order.status === 'cancelled';
}

/** Events newest first; the API returns them oldest first. */
export function newestFirst<T extends { createdAt: string }>(events: readonly T[]): T[] {
  return [...events].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/**
 * Why a provider refund failed. The refund row carries only its status; the reason is on the
 * `refund_failed` timeline event (`data.refundId`, `data.reason`), newest first.
 */
export function refundFailureReason(
  events: readonly OrderEvent[],
  refundId: string,
): string | null {
  for (const event of newestFirst(events)) {
    if (event.kind !== 'refund_failed' || event.data?.refundId !== refundId) continue;
    const reason = event.data.reason;
    return typeof reason === 'string' && reason !== '' ? reason : null;
  }
  return null;
}

/** "Visa •••• 4242", with the wallet when there is one; null when the provider sent nothing. */
export function cardSummary(card: OrderPaymentCard): string | null {
  if (!card) return null;
  const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : null;
  const last4 = card.last4 ? `•••• ${card.last4}` : null;
  const main = [brand, last4].filter(Boolean).join(' ');
  if (!main) return null;
  return card.wallet ? `${main} (${card.wallet.replace(/_/g, ' ')})` : main;
}
